import {
  IFdApplicationType,
  IFdCompany,
  IFdFarm,
  IFdFundingRequestCost,
  IFdProductionPlan,
  IFdProductionHistory,
  IFdFinancialPerformance,
  IFdFarmAssetType,
  IFdLiability,
  IFdApplicationDocType,
  ITraderApplication,
  ISupplierCompanyProfile,
} from 'constants/interfaces';

import {
  lshExtractBankingDetailsFromTraderApplication,
  lshExtractBbbeeLevelFromTraderApplication,
  lshExtractRegistrationNumberFromTraderApplication,
  lshExtractRelatedPartiesFromTraderApplication,
  lshExtractTaxNumberFromTraderApplication,
  lshExtractTradingNameFromTraderApplication,
  lshExtractVatNumberFromTraderApplication,
} from './tradingFormJourneyUtils';

export interface ISupplierJourneyLivestockRecord {
  id: string;
  quantity?: number;
  type?: string;
  productGrading?: string;
  sellOfPeriod?: string;
  price?: number;
  notes?: string;
  livestock?: {
    id?: string;
    name?: string;
    livestockType?: string;
    livestockProduct?: string;
  };
}

export interface ISupplierJourneyWaterRightsRecord {
  id: string;
  waterSource?: string | string[];
  householdConsumption?: string | string[];
  notes?: string;
  waterRights?: boolean;
  waterScale?: string;
  permitNumber?: string;
  controlled?: boolean;
  waterQuantity?: number;
  scheme?: string;
  schedule?: string;
}

export interface ISupplierJourneyLongTermCropRecord {
  id: string;
  blockNumber?: string;
  age?: number;
  numberOfTrees?: number;
  area?: number;
  trellies?: string;
  spacing?: string;
  irrigation?: string;
  type?: string;
  notes?: string;
  projectedYear?: number;
  projectedYield?: number;
  projectedPrice?: number;
  projectedArea?: number;
  historyYear1?: number;
  historyYield1?: number;
  historyArea1?: number;
  historyYear2?: number;
  historyYield2?: number;
  historyArea2?: number;
  historyYear3?: number;
  historyYield3?: number;
  historyArea3?: number;
  crop?: {
    id?: string;
    name?: string;
  };
}

export interface ISupplierJourneyPropertyRecord {
  id: string;
  nameOfDeed?: string;
  areaDistrict?: string;
  farmArrangement?: string;
  term?: string;
  amount?: number;
  dryland?: number;
  irrigation?: number;
  pastures?: number;
  grazing?: number;
  intensive?: number;
  other?: number;
  notes?: string;
  total?: number;
}

export interface ISupplierJourneyApplication extends IFdApplicationType {
  completedSections?: string[];
  completedDocTypesPercentage?: number;
  incompleteDocTypes?: string[];
  applicationLivestock?: ISupplierJourneyLivestockRecord[];
  applicationWaterRights?: ISupplierJourneyWaterRightsRecord[];
  applicationLongTermCrops?: ISupplierJourneyLongTermCropRecord[];
  applicationProperties?: ISupplierJourneyPropertyRecord[];
}

export interface ISupplierJourneyRelatedPartySummary {
  id: string;
  fullName: string;
  idNumber: string;
  phone: string;
  address: string;
}

export interface ISupplierJourneyApplicantSummary {
  fullName: string;
  email: string;
  phoneNumber: string;
  idNumber: string;
  creditScore: number | null;
  externalId: string;
}

export interface ISupplierJourneyCompanySummary {
  displayName: string;
  registrationName: string;
  registrationNumber: string;
  tradingName: string;
  vatNumber: string;
  taxNumber: string;
  registeredAddress: string;
  operatingAddress: string;
  bankName: string;
  bankAccountType: string;
  bankAccountNumber: string;
  bankBranchCode: string;
  bbbeeStatus: string;
  relatedParties: ISupplierJourneyRelatedPartySummary[];
}

export interface ISupplierJourneyFarmSummary {
  name: string;
  address: string;
  province: string;
  description: string;
  leasedHectares: number;
  ownedHectares: number;
  irrigatedHectares: number;
  drylandHectares: number;
  leaseEndDate: number | null;
  waterRights: boolean;
  shortTermGoals: string;
  longTermGoals: string;
  landComposition: string;
  workforceNumber: number;
  teamCount: number;
  customerCount: number;
  supplierCount: number;
  riskCount: number;
}

export interface ISupplierJourneyProductionSummary {
  commodityNames: string[];
  planCount: number;
  historyCount: number;
  livestockCount: number;
  waterRightsCount: number;
  longTermCropCount: number;
  propertyCount: number;
  planYears: number[];
  historyYears: number[];
  projectedCropNames: string[];
  historicCropNames: string[];
}

export interface ISupplierJourneyFinancialLineSummary {
  label: string;
  cost: number;
}

export interface ISupplierJourneyFinancialSummary {
  fundingAskTotal: number;
  fundingAskLines: ISupplierJourneyFinancialLineSummary[];
  revenueTotal: number;
  expensesTotal: number;
  operatingCostsTotal: number;
  profitTotal: number;
  assetValueTotal: number;
  liabilityBalanceTotal: number;
  liabilityInstallmentTotal: number;
  financialYears: number[];
}

export interface ISupplierJourneyDocumentsSummary {
  totalCount: number;
  uploadedCount: number;
  outstandingCount: number;
  documentTypes: string[];
  incompleteDocumentTypes: string[];
}

export interface ISupplierJourneyMeta {
  completionPercent: number;
  applicationState: string;
  isCompleted: boolean;
  progress: number;
  subProgress: number;
  incompleteSections: string[];
  missingSections: string[];
}

export interface ISupplierJourneySnapshot {
  applicant: ISupplierJourneyApplicantSummary;
  company: ISupplierJourneyCompanySummary;
  farm: ISupplierJourneyFarmSummary;
  production: ISupplierJourneyProductionSummary;
  financials: ISupplierJourneyFinancialSummary;
  documents: ISupplierJourneyDocumentsSummary;
  journey: ISupplierJourneyMeta;
}

function lshToNumber(value: unknown): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function lshToText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function lshBuildAddress(parts: Array<string | undefined | null>): string {
  return parts.map(lshToText).filter(Boolean).join(', ');
}

function lshGetCompanyAddress(
  traderCompany?: ISupplierCompanyProfile,
  fallbackCompany?: IFdCompany,
): string {
  const supplierAddress = lshBuildAddress([
    traderCompany?.addressLine1,
    traderCompany?.addressLine2,
    traderCompany?.city,
    traderCompany?.province,
    traderCompany?.areaCode,
  ]);

  return supplierAddress || lshToText(fallbackCompany?.address);
}

function lshGetFarmAddress(farm?: IFdFarm): string {
  return lshBuildAddress([farm?.address, farm?.province]);
}

function lshExtractRelatedParties(
  traderApplication?: ITraderApplication,
): ISupplierJourneyRelatedPartySummary[] {
  return lshExtractRelatedPartiesFromTraderApplication(traderApplication).map(
    (party, index) => ({
      id: lshToText(party.id || index + 1),
      fullName: lshToText(party.fullName),
      idNumber: lshToText(party.idNumber),
      phone: lshToText(party.phone),
      address: lshToText(party.address),
    }),
  );
}

function lshExtractCompanyRegistrationNumber(
  application?: ISupplierJourneyApplication,
  traderApplication?: ITraderApplication,
): string {
  return (
    lshToText((application as any)?.companyRegistrationNumber) ||
    lshToText((application?.company as any)?.companyRegistrationNumber) ||
    lshToText((application?.company as any)?.companyRegNumber) ||
    lshToText((application?.company as any)?.registrationNumber) ||
    lshExtractRegistrationNumberFromTraderApplication(traderApplication)
  );
}

function lshExtractBbbeeStatus(traderApplication?: ITraderApplication): string {
  return lshExtractBbbeeLevelFromTraderApplication(traderApplication);
}

export function lshResolveJourneyCompletionPercent(
  application?: ISupplierJourneyApplication,
): number {
  if (!application) return 0;

  const completedSectionsPercentage = Number(
    application.completedSectionsPercentage,
  );

  if (Number.isFinite(completedSectionsPercentage)) {
    return Math.max(
      0,
      Math.min(
        100,
        completedSectionsPercentage <= 1
          ? Math.round(completedSectionsPercentage * 100)
          : Math.round(completedSectionsPercentage),
      ),
    );
  }

  const progress = Number(application.progress || 0);
  if (Number.isFinite(progress) && progress > 0) {
    if (progress > 8) {
      return Math.max(0, Math.min(100, Math.round(progress)));
    }

    const sectionCount = 7;
    const completedSections = Math.max(
      0,
      Math.min(sectionCount, Math.round(progress) - 1),
    );
    const sectionSize = 100 / sectionCount;
    const rawSubProgress = Number(application.subProgress ?? 0);
    const subProgress = Number.isFinite(rawSubProgress)
      ? Math.max(0, Math.min(100, rawSubProgress))
      : 0;
    const percent =
      completedSections * sectionSize +
      (completedSections < sectionCount
        ? (subProgress / 100) * sectionSize
        : 0);

    return Math.max(0, Math.min(100, Math.round(percent)));
  }

  if (application.completed) return 100;

  return 0;
}

export function lshGetSupplierTraderApplication(
  applications?: ITraderApplication[] | null,
): ITraderApplication | undefined {
  return applications?.find(
    (application) =>
      String(application?.tradeType || '').toUpperCase() === 'SUPPLIER',
  );
}

export function lshAdaptApplicantSummary(
  application?: ISupplierJourneyApplication,
): ISupplierJourneyApplicantSummary {
  const applicant = application?.applyingUser;
  const fullName = [lshToText(applicant?.name), lshToText(applicant?.surname)]
    .filter(Boolean)
    .join(' ');

  return {
    fullName,
    email: lshToText(applicant?.email),
    phoneNumber: lshToText(applicant?.phoneNumber),
    idNumber: lshToText(applicant?.idNumber),
    creditScore: Number.isFinite(Number(applicant?.creditScore))
      ? Number(applicant?.creditScore)
      : null,
    externalId: lshToText(applicant?.externalId),
  };
}

export function lshAdaptCompanySummary(
  application?: ISupplierJourneyApplication,
  traderApplication?: ITraderApplication,
): ISupplierJourneyCompanySummary {
  const supplierCompany = traderApplication?.company;
  const fallbackCompany = application?.company;
  const relatedParties = lshExtractRelatedParties(traderApplication);
  const bankingFromTraderForm =
    lshExtractBankingDetailsFromTraderApplication(traderApplication);
  const registrationNumber = lshExtractCompanyRegistrationNumber(
    application,
    traderApplication,
  );
  const bbbeeStatus = lshExtractBbbeeStatus(traderApplication);
  const displayName =
    lshToText(supplierCompany?.name) || lshToText(fallbackCompany?.name) || '';

  return {
    displayName,
    registrationName:
      lshToText(supplierCompany?.name) || lshToText(fallbackCompany?.name),
    registrationNumber,
    tradingName: lshToText(
      lshExtractTradingNameFromTraderApplication(traderApplication) ||
        supplierCompany?.tradingName,
    ),
    vatNumber: lshToText(
      lshExtractVatNumberFromTraderApplication(traderApplication) ||
        supplierCompany?.vatNumber,
    ),
    taxNumber: lshToText(
      lshExtractTaxNumberFromTraderApplication(traderApplication) ||
        supplierCompany?.taxNumber,
    ),
    registeredAddress: lshGetCompanyAddress(supplierCompany, fallbackCompany),
    operatingAddress: lshGetCompanyAddress(supplierCompany, fallbackCompany),
    bankName:
      lshToText(bankingFromTraderForm.bankName) ||
      lshToText(supplierCompany?.bank?.name),
    bankAccountType:
      lshToText(bankingFromTraderForm.accountType) ||
      lshToText(supplierCompany?.bankAccountType?.name),
    bankAccountNumber:
      lshToText(bankingFromTraderForm.accountNumber) ||
      lshToText(supplierCompany?.bankAccountNumber),
    bankBranchCode:
      lshToText(bankingFromTraderForm.branchCode) ||
      lshToText(supplierCompany?.bankBranchCode),
    bbbeeStatus,
    relatedParties,
  };
}

export function lshAdaptFarmSummary(
  application?: ISupplierJourneyApplication,
): ISupplierJourneyFarmSummary {
  const farm = application?.farm;

  return {
    name: lshToText(farm?.name),
    address: lshGetFarmAddress(farm),
    province: lshToText(farm?.province),
    description: lshToText(farm?.description),
    leasedHectares: lshToNumber(farm?.numberOfLeasedHectares),
    ownedHectares: lshToNumber(farm?.ownedHectares),
    irrigatedHectares: lshToNumber(farm?.irrigatedHectares),
    drylandHectares: lshToNumber(farm?.drylandHectares),
    leaseEndDate: farm?.leaseEndDate ?? null,
    waterRights: Boolean(farm?.waterRights),
    shortTermGoals: lshToText(farm?.shortTermGoals),
    longTermGoals: lshToText(farm?.longTermGoals),
    landComposition: lshToText(farm?.landComposition),
    workforceNumber: lshToNumber(farm?.workforceNumber),
    teamCount: application?.team?.length || 0,
    customerCount: application?.customers?.length || 0,
    supplierCount: application?.suppliers?.length || 0,
    riskCount: application?.farmRisk?.length || 0,
  };
}

function lshGetUniqueSortedNumbers(values: number[]): number[] {
  return Array.from(
    new Set(values.filter((value) => Number.isFinite(value))),
  ).sort((left, right) => left - right);
}

function lshGetCommodityNames(
  productionPlans?: IFdProductionPlan[],
  productionHistory?: IFdProductionHistory[],
  longTermCrops?: ISupplierJourneyLongTermCropRecord[],
): string[] {
  const names = [
    ...(productionPlans || []).map((item) => lshToText(item.crop?.name)),
    ...(productionHistory || []).map((item) => lshToText(item.crop?.name)),
    ...(longTermCrops || []).map((item) => lshToText(item.crop?.name)),
  ].filter(Boolean);

  return Array.from(new Set(names)).sort((left, right) =>
    left.localeCompare(right),
  );
}

export function lshAdaptProductionSummary(
  application?: ISupplierJourneyApplication,
): ISupplierJourneyProductionSummary {
  const productionPlans = application?.productionPlans || [];
  const productionHistory = application?.productionHistory || [];
  const longTermCrops = application?.applicationLongTermCrops || [];

  return {
    commodityNames: lshGetCommodityNames(
      productionPlans,
      productionHistory,
      longTermCrops,
    ),
    planCount: productionPlans.length,
    historyCount: productionHistory.length,
    livestockCount: application?.applicationLivestock?.length || 0,
    waterRightsCount: application?.applicationWaterRights?.length || 0,
    longTermCropCount: longTermCrops.length,
    propertyCount: application?.applicationProperties?.length || 0,
    planYears: lshGetUniqueSortedNumbers(
      productionPlans.map((item) =>
        item.plantingDate ? new Date(item.plantingDate).getFullYear() : NaN,
      ),
    ),
    historyYears: lshGetUniqueSortedNumbers(
      productionHistory.map((item) => lshToNumber(item.year)),
    ),
    projectedCropNames: Array.from(
      new Set(
        productionPlans
          .map((item) => lshToText(item.crop?.name))
          .filter(Boolean),
      ),
    ),
    historicCropNames: Array.from(
      new Set(
        productionHistory
          .map((item) => lshToText(item.crop?.name))
          .filter(Boolean),
      ),
    ),
  };
}

function lshAdaptFundingAskLines(
  lines?: IFdFundingRequestCost[],
): ISupplierJourneyFinancialLineSummary[] {
  return (lines || []).map((line) => ({
    label: lshToText(line.label),
    cost: lshToNumber(line.cost),
  }));
}

function lshSumFinancialPerformance(
  records: IFdFinancialPerformance[] | undefined,
  key: keyof Pick<
    IFdFinancialPerformance,
    'revenue' | 'expenses' | 'operatingCosts' | 'profit'
  >,
): number {
  return (records || []).reduce(
    (total, record) => total + lshToNumber(record[key]),
    0,
  );
}

function lshSumAssetValues(records?: IFdFarmAssetType[]): number {
  return (records || []).reduce(
    (total, record) => total + lshToNumber(record.value),
    0,
  );
}

function lshSumLiabilityValues(
  records: IFdLiability[] | undefined,
  key: keyof Pick<
    IFdLiability,
    'currentBalanceLimit' | 'monthlyInstallmentAmount'
  >,
): number {
  return (records || []).reduce(
    (total, record) => total + lshToNumber(record[key]),
    0,
  );
}

export function lshAdaptFinancialSummary(
  application?: ISupplierJourneyApplication,
): ISupplierJourneyFinancialSummary {
  const fundingAskLines = lshAdaptFundingAskLines(
    application?.fundingAsk?.fundingRequestCosts,
  );

  return {
    fundingAskTotal: fundingAskLines.reduce(
      (total, line) => total + line.cost,
      0,
    ),
    fundingAskLines,
    revenueTotal: lshSumFinancialPerformance(
      application?.financialPerformance,
      'revenue',
    ),
    expensesTotal: lshSumFinancialPerformance(
      application?.financialPerformance,
      'expenses',
    ),
    operatingCostsTotal: lshSumFinancialPerformance(
      application?.financialPerformance,
      'operatingCosts',
    ),
    profitTotal: lshSumFinancialPerformance(
      application?.financialPerformance,
      'profit',
    ),
    assetValueTotal: lshSumAssetValues(
      application?.assets || application?.farmAsset,
    ),
    liabilityBalanceTotal: lshSumLiabilityValues(
      application?.liabilities,
      'currentBalanceLimit',
    ),
    liabilityInstallmentTotal: lshSumLiabilityValues(
      application?.liabilities,
      'monthlyInstallmentAmount',
    ),
    financialYears: lshGetUniqueSortedNumbers(
      (application?.financialPerformance || []).map((record) =>
        lshToNumber(record.year),
      ),
    ),
  };
}

export function lshAdaptDocumentsSummary(
  application?: ISupplierJourneyApplication,
): ISupplierJourneyDocumentsSummary {
  const documents = (application?.applicationDocuments ||
    []) as IFdApplicationDocType[];
  const incompleteDocumentTypes = (application?.incompleteDocTypes || [])
    .map(lshToText)
    .filter(Boolean);

  return {
    totalCount: documents.length + incompleteDocumentTypes.length,
    uploadedCount: documents.length,
    outstandingCount: Math.max(incompleteDocumentTypes.length, 0),
    documentTypes: documents
      .map((document) => lshToText(document.documentType))
      .filter(Boolean),
    incompleteDocumentTypes,
  };
}

export function lshAdaptJourneyMeta(
  application?: ISupplierJourneyApplication,
): ISupplierJourneyMeta {
  return {
    completionPercent: lshResolveJourneyCompletionPercent(application),
    applicationState: lshToText(application?.applicationState),
    isCompleted: Boolean(application?.completed),
    progress: lshToNumber(application?.progress),
    subProgress: lshToNumber(application?.subProgress),
    incompleteSections: (application?.incompleteSections || [])
      .map(lshToText)
      .filter(Boolean),
    missingSections: (application?.incompleteSections || [])
      .map(lshToText)
      .filter(Boolean),
  };
}

export function lshAdaptSupplierJourneySnapshot(
  application?: ISupplierJourneyApplication,
  traderApplication?: ITraderApplication,
): ISupplierJourneySnapshot {
  return {
    applicant: lshAdaptApplicantSummary(application),
    company: lshAdaptCompanySummary(application, traderApplication),
    farm: lshAdaptFarmSummary(application),
    production: lshAdaptProductionSummary(application),
    financials: lshAdaptFinancialSummary(application),
    documents: lshAdaptDocumentsSummary(application),
    journey: lshAdaptJourneyMeta(application),
  };
}

export {
  lshResolveJourneyCompletionPercent as resolveJourneyCompletionPercent,
  lshGetSupplierTraderApplication as getSupplierTraderApplication,
  lshAdaptApplicantSummary as adaptApplicantSummary,
  lshAdaptCompanySummary as adaptCompanySummary,
  lshAdaptFarmSummary as adaptFarmSummary,
  lshAdaptProductionSummary as adaptProductionSummary,
  lshAdaptFinancialSummary as adaptFinancialSummary,
  lshAdaptDocumentsSummary as adaptDocumentsSummary,
  lshAdaptJourneyMeta as adaptJourneyMeta,
  lshAdaptSupplierJourneySnapshot as adaptSupplierJourneySnapshot,
};
